import type { Policy, PolicyCreateData } from '../../services/policiesApi';
import type { Zone } from '../../services/zonesApi';

export type PolicySubject = 'person' | 'vehicle' | 'forklift' | 'all';
export type PackKey = 'core_safety' | 'forklift_safety' | 'worker_protection';
export type ScopeKind = 'zones' | 'line' | 'whole_view_optional';
export type ZoneSemanticHint = 'restricted' | 'pedestrian_only' | 'forklift_only' | 'shared';

export type PolicyUiContext = {
  pack_key?: PackKey;
  policy_alias?: string;
  version?: number;
  [key: string]: unknown;
};

export type InvestigationAlignmentContextBlock = {
  occurrence_families: string[];
  cause_patterns: string[];
  contributing_factor_themes: string[];
  ux_summary: string;
  report_summary: string;
  claim_boundary: string;
  source_label?: string;
};

export type ClaimsRoiAlignmentContextBlock = {
  claims_alignment_category: string;
  claims_cost_driver_family?: string;
  commercial_priority: string;
  target_industry_hints?: string[];
  roi_rationale?: string;
  ux_summary: string;
  report_summary: string;
  claim_boundary: string;
  source_label?: string;
  industry_sector_hints?: string[];
  classification_unit_hints?: string[];
  recommended_pilot_templates?: string[];
  recommended_first_scan?: string | null;
  buyer_pain_language?: string;
  sales_discovery_questions?: string[];
  benefit_type_relevance?: string[];
  claim_characteristic_hints?: string[];
  commercial_priority_basis?: string[];
};

export type SafetyControlReviewContextBlock = {
  control_objective: string;
  observed_exposure?: string;
  existing_controls_to_verify: string[];
  potential_control_gaps: string[];
  field_verification_questions: string[];
  verification_status: string;
  effectiveness_boundary: string;
  closure_requirements: string[];
  claim_boundary: string;
  ux_summary: string;
  report_summary: string;
  source_label?: string;
  primary_control_lens?: string;
};

export type PolicyReportContextBlock = {
  scan_template: string;
  exposure_category: string;
  risk_family: string;
  hazard_exposure_label: string;
  short_exposure_label: string;
  control_review_focus: string[];
  claim_boundary_profile: string;
  scan_template_label?: string;
  exposure_category_label?: string;
  evidence_supports_review?: string;
  report_title_suffix?: string | null;
  investigation_alignment?: InvestigationAlignmentContextBlock | null;
  claims_roi_alignment?: ClaimsRoiAlignmentContextBlock | null;
  safety_control_review?: SafetyControlReviewContextBlock | null;
};

export type PolicyAliasDefinition = {
  alias: string;
  label: string;
  description: string;
  packKey: PackKey;
  groupLabel?: string;
  groupDescription?: string;
  policyType?: PolicyCreateData['policy_type'];
  entityTypes?: PolicySubject[];
  scopeKind?: ScopeKind;
  defaults?: {
    config?: Record<string, unknown>;
    geometryMode?: 'zones' | 'whole_view';
  };
  zoneSemanticHint?: ZoneSemanticHint;
  disabled?: boolean;
};

export type PackDefinition = {
  key: PackKey;
  label: string;
  valueProp: string;
  bestFor: string;
  cameraHint: string;
  policies: PolicyAliasDefinition[];
};

export const PACK_DISPLAY_ORDER: PackKey[] = ['core_safety', 'forklift_safety', 'worker_protection'];

export const GENERIC_POLICY_TYPE_LABELS: Record<PolicyCreateData['policy_type'], string> = {
  ppe_compliance: 'PPE Compliance',
  zone_intrusion: 'Restricted Area Exposure',
  dwell_violation: 'Dwell Time Violation',
  loitering: 'Loitering',
  occupancy: 'Occupancy',
  worker_down: 'Worker Down',
  forklift_proximity: 'Unsafe Forklift-Pedestrian Proximity',
  pedestrian_near_moving_forklift: 'Pedestrian Near Moving Forklift',
  forklift_speeding: 'Forklift Speeding',
  line_crossing: 'Line Crossing',
  region_entrance: 'Region Entrance',
  region_exiting: 'Region Exiting',
  composite_alert: 'Composite Alert',
};

export const POLICY_PACKS: PackDefinition[] = [
  {
    key: 'core_safety',
    label: 'Core Safety',
    valueProp: 'People-focused protections for PPE, zones, entrances, and occupancy.',
    bestFor: 'General warehouse safety rollouts and pedestrian coverage.',
    cameraHint: 'Best with cameras covering entrances, aisles, and work areas.',
    policies: [
      {
        alias: 'ppe_compliance',
        label: 'PPE Compliance',
        description: 'Detect missing required PPE on people.',
        packKey: 'core_safety',
        policyType: 'ppe_compliance',
        entityTypes: ['person'],
        scopeKind: 'whole_view_optional',
        defaults: { geometryMode: 'whole_view', config: { required_items: ['helmet', 'vest'] } },
      },
      {
        alias: 'zone_intrusion',
        label: 'Restricted Area Exposure',
        description: 'Person enters a configured restricted or protected zone.',
        packKey: 'core_safety',
        policyType: 'zone_intrusion',
        entityTypes: ['person'],
        scopeKind: 'zones',
      },
      {
        alias: 'line_crossing',
        label: 'Line Crossing',
        description: 'Person crosses a monitored line.',
        packKey: 'core_safety',
        policyType: 'line_crossing',
        entityTypes: ['person'],
        scopeKind: 'line',
      },
      {
        alias: 'dwell_violation',
        label: 'Dwell Time Violation',
        description: 'Person stays too long in a zone.',
        packKey: 'core_safety',
        policyType: 'dwell_violation',
        entityTypes: ['person'],
        scopeKind: 'zones',
      },
      {
        alias: 'loitering',
        label: 'Loitering',
        description: 'Person lingers in an area.',
        packKey: 'core_safety',
        policyType: 'loitering',
        entityTypes: ['person'],
        scopeKind: 'zones',
      },
      {
        alias: 'occupancy',
        label: 'Occupancy',
        description: 'Person count exceeds the allowed limit.',
        packKey: 'core_safety',
        policyType: 'occupancy',
        entityTypes: ['person'],
        scopeKind: 'whole_view_optional',
        defaults: { geometryMode: 'whole_view' },
      },
      {
        alias: 'region_entrance',
        label: 'Region Entrance',
        description: 'Person enters a monitored region.',
        packKey: 'core_safety',
        policyType: 'region_entrance',
        entityTypes: ['person'],
        scopeKind: 'zones',
      },
      {
        alias: 'region_exiting',
        label: 'Region Exiting',
        description: 'Person exits a monitored region.',
        packKey: 'core_safety',
        policyType: 'region_exiting',
        entityTypes: ['person'],
        scopeKind: 'zones',
      },
    ],
  },
  {
    key: 'forklift_safety',
    label: 'Forklift Safety',
    valueProp: 'Interactions between forklifts and people in shared warehouse space.',
    bestFor: 'Shared aisles, staging areas, and mixed pedestrian / forklift traffic.',
    cameraHint: 'Best with cameras covering lanes, docks, and shared loading zones.',
    policies: [
      {
        alias: 'pedestrian_in_forklift_zone',
        label: 'Pedestrian in Forklift Zone',
        description: 'Person enters a forklift-only area.',
        packKey: 'forklift_safety',
        groupLabel: 'Spatial Separation',
        groupDescription: 'Wave 1 protections for lane boundaries, restricted access, and static enforcement.',
        policyType: 'zone_intrusion',
        entityTypes: ['person'],
        scopeKind: 'zones',
        zoneSemanticHint: 'forklift_only',
      },
      {
        alias: 'forklift_in_pedestrian_zone',
        label: 'Forklift in Pedestrian Zone',
        description: 'Forklift enters a pedestrian-priority area.',
        packKey: 'forklift_safety',
        groupLabel: 'Spatial Separation',
        groupDescription: 'Wave 1 protections for lane boundaries, restricted access, and static enforcement.',
        policyType: 'zone_intrusion',
        entityTypes: ['forklift'],
        scopeKind: 'zones',
        zoneSemanticHint: 'pedestrian_only',
      },
      {
        alias: 'forklift_in_restricted_area',
        label: 'Forklift in Restricted Area',
        description: 'Forklift enters a restricted or no-go area.',
        packKey: 'forklift_safety',
        groupLabel: 'Spatial Separation',
        groupDescription: 'Wave 1 protections for lane boundaries, restricted access, and static enforcement.',
        policyType: 'zone_intrusion',
        entityTypes: ['forklift'],
        scopeKind: 'zones',
        zoneSemanticHint: 'restricted',
      },
      {
        alias: 'forklift_line_crossing',
        label: 'Forklift Line Crossing',
        description: 'Forklift crosses a monitored line.',
        packKey: 'forklift_safety',
        groupLabel: 'Spatial Separation',
        groupDescription: 'Wave 1 protections for lane boundaries, restricted access, and static enforcement.',
        policyType: 'line_crossing',
        entityTypes: ['forklift'],
        scopeKind: 'line',
      },
      {
        alias: 'forklift_dwell',
        label: 'Forklift Dwell',
        description: 'Forklift remains in a zone too long.',
        packKey: 'forklift_safety',
        groupLabel: 'Spatial Separation',
        groupDescription: 'Wave 1 protections for lane boundaries, restricted access, and static enforcement.',
        policyType: 'dwell_violation',
        entityTypes: ['forklift'],
        scopeKind: 'zones',
      },
      {
        alias: 'forklift_proximity',
        label: 'Unsafe Forklift-Pedestrian Proximity',
        description: 'Pedestrian and forklift remain too close for too long.',
        packKey: 'forklift_safety',
        groupLabel: 'Advanced Interaction Risk',
        groupDescription: 'Wave 2 motion-aware policies for unsafe spacing, active interaction risk, and monitored speed.',
        policyType: 'forklift_proximity',
        entityTypes: ['person'],
        // C1: pilot preset requires one customer-defined traffic zone (Epic 40 whole-view still allowed outside pilot defaults).
        scopeKind: 'zones',
        defaults: { geometryMode: 'zones', config: { behavior_rule: { mode: 'proximity', sensitivity: 'medium' } } },
      },
      {
        alias: 'pedestrian_near_moving_forklift',
        label: 'Pedestrian Near Moving Forklift',
        description: 'Person gets too close to a forklift that is actively moving.',
        packKey: 'forklift_safety',
        groupLabel: 'Advanced Interaction Risk',
        groupDescription: 'Wave 2 motion-aware policies for unsafe spacing, active interaction risk, and monitored speed.',
        policyType: 'pedestrian_near_moving_forklift',
        entityTypes: ['person'],
        scopeKind: 'whole_view_optional',
        defaults: { geometryMode: 'whole_view', config: { behavior_rule: { mode: 'near_moving', sensitivity: 'medium' } } },
      },
      {
        alias: 'forklift_speeding',
        label: 'Forklift Speeding',
        description: 'Forklift exceeds configured motion threshold in a monitored area.',
        packKey: 'forklift_safety',
        groupLabel: 'Advanced Interaction Risk',
        groupDescription: 'Wave 2 motion-aware policies for unsafe spacing, active interaction risk, and monitored speed.',
        policyType: 'forklift_speeding',
        entityTypes: ['forklift'],
        scopeKind: 'whole_view_optional',
        defaults: { geometryMode: 'whole_view', config: { behavior_rule: { mode: 'speeding', sensitivity: 'medium' } } },
      },
    ],
  },
  {
    key: 'worker_protection',
    label: 'Worker Protection',
    valueProp: 'Staged worker-safety protections for down-state review, with fall detection deferred until later launch stages.',
    bestFor: 'Worker-protection cameras covering lone-worker zones and high-risk areas with posture-friendly viewpoints.',
    cameraHint: 'Requires posture-sensitive camera placement and tuned viewpoints.',
    policies: [
      {
        alias: 'worker_down',
        label: 'Worker Down',
        description: 'Detect an immobile worker-down event.',
        packKey: 'worker_protection',
        groupLabel: 'Staged Launch',
        groupDescription: 'Initial Worker Protection rollout focused on Worker Down first, with stricter runtime and validation gates before operational use.',
        policyType: 'worker_down',
        entityTypes: ['person'],
        scopeKind: 'whole_view_optional',
        defaults: { geometryMode: 'whole_view', config: { behavior_rule: { mode: 'worker_down', sensitivity: 'medium' } } },
      },
      {
        alias: 'worker_fall',
        label: 'Worker Fall (coming soon)',
        description: 'Detect a worker fall event.',
        packKey: 'worker_protection',
        disabled: true,
      },
    ],
  },
];

export const ACTIVE_POLICY_PACKS = POLICY_PACKS.filter((pack) =>
  pack.policies.some((policy) => !policy.disabled && policy.policyType),
);

export const PACK_BY_KEY = POLICY_PACKS.reduce<Record<PackKey, PackDefinition>>((acc, pack) => {
  acc[pack.key] = pack;
  return acc;
}, {} as Record<PackKey, PackDefinition>);

export const POLICY_ALIAS_BY_KEY = POLICY_PACKS.reduce<Record<string, PolicyAliasDefinition>>((acc, pack) => {
  pack.policies.forEach((policy) => {
    acc[policy.alias] = policy;
  });
  return acc;
}, {});

export function getPackDefinition(packKey: PackKey): PackDefinition {
  return PACK_BY_KEY[packKey];
}

export function getPolicyAliasDefinition(alias: string): PolicyAliasDefinition | undefined {
  return POLICY_ALIAS_BY_KEY[String(alias || '').trim()];
}

export function getPoliciesForPack(packKey: PackKey): PolicyAliasDefinition[] {
  return PACK_BY_KEY[packKey]?.policies || [];
}

export function isPolicyAliasSelectable(
  policyAlias: PolicyAliasDefinition | undefined,
): policyAlias is PolicyAliasDefinition & { policyType: PolicyCreateData['policy_type'] } {
  return Boolean(policyAlias && !policyAlias.disabled && policyAlias.policyType);
}

export function getPackKeyForAlias(alias: string): PackKey {
  const definition = getPolicyAliasDefinition(alias);
  return definition?.packKey || 'core_safety';
}

export function getPolicySubjectFromEntityTypes(entityTypes: unknown): PolicySubject {
  const normalized = (Array.isArray(entityTypes) ? entityTypes : [])
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);
  if (normalized.length === 0) return 'all';
  const first = normalized[0];
  if (first === 'person' || first === 'vehicle' || first === 'forklift' || first === 'all') {
    return first as PolicySubject;
  }
  return 'person';
}

export function readPolicyUiContext(policy: Pick<Policy, 'config'> | null | undefined): PolicyUiContext | null {
  const candidate = policy?.config?.ui_context;
  if (!candidate || typeof candidate !== 'object') return null;
  const next: PolicyUiContext = { ...(candidate as Record<string, unknown>) };
  return next;
}

export function withPolicyUiContext(
  config: Record<string, any> | undefined,
  uiContext: Required<Pick<PolicyUiContext, 'pack_key' | 'policy_alias' | 'version'>>,
): Record<string, any> {
  const base = config && typeof config === 'object' ? { ...config } : {};
  const existingUiContext =
    base.ui_context && typeof base.ui_context === 'object' ? { ...(base.ui_context as Record<string, unknown>) } : {};
  return {
    ...base,
    ui_context: {
      ...existingUiContext,
      ...uiContext,
    },
  };
}

const REPORT_CONTEXT_BY_ALIAS: Record<string, PolicyReportContextBlock> = {
  zone_intrusion: {
    scan_template: 'restricted_area_exposure',
    exposure_category: 'person_in_restricted_area',
    risk_family: 'restricted_area_access_control',
    hazard_exposure_label: 'Person activity in configured restricted area',
    short_exposure_label: 'Person activity in restricted area',
    control_review_focus: [
      'restricted-area boundary',
      'access-control procedure',
      'signage',
      'floor markings',
      'supervision',
      'workflow routing',
    ],
    claim_boundary_profile: 'restricted_area_access_review',
    evidence_supports_review:
      'Supports review of restricted-area access controls, boundary clarity, and workflow routing. Field context and existing controls require confirmation.',
    investigation_alignment: {
      occurrence_families: ['Restricted-area access exposure', 'Hazardous-area access review'],
      cause_patterns: ['boundary clarity', 'unauthorized access path', 'workflow routed through restricted space'],
      contributing_factor_themes: [
        'restricted-area boundary',
        'access-control procedure',
        'signage and floor markings',
        'supervision and workflow routing',
      ],
      ux_summary: 'Aligned to restricted-area / hazardous-area access review themes for investigation triage.',
      report_summary:
        'Comparable to restricted-area access and hazardous-area review themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  person_in_restricted_area: {
    scan_template: 'restricted_area_exposure',
    exposure_category: 'person_in_restricted_area',
    risk_family: 'restricted_area_access_control',
    hazard_exposure_label: 'Person activity in configured restricted area',
    short_exposure_label: 'Person activity in restricted area',
    control_review_focus: [
      'restricted-area boundary',
      'access-control procedure',
      'signage',
      'floor markings',
      'supervision',
      'workflow routing',
    ],
    claim_boundary_profile: 'restricted_area_access_review',
    investigation_alignment: {
      occurrence_families: ['Restricted-area access exposure', 'Hazardous-area access review'],
      cause_patterns: ['boundary clarity', 'unauthorized access path', 'workflow routed through restricted space'],
      contributing_factor_themes: [
        'restricted-area boundary',
        'access-control procedure',
        'signage and floor markings',
        'supervision and workflow routing',
      ],
      ux_summary: 'Aligned to restricted-area / hazardous-area access review themes for investigation triage.',
      report_summary:
        'Comparable to restricted-area access and hazardous-area review themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  pedestrian_in_forklift_zone: {
    scan_template: 'mobile_equipment_interaction',
    exposure_category: 'pedestrian_in_forklift_zone',
    risk_family: 'struck_by_mobile_equipment',
    hazard_exposure_label: 'Pedestrian activity in restricted forklift zone',
    short_exposure_label: 'Pedestrian exposure in forklift zone',
    control_review_focus: [
      'pedestrian separation',
      'restricted-zone layout',
      'route design',
      'floor markings',
      'signage',
      'barriers or physical separation',
      'supervisor review',
    ],
    claim_boundary_profile: 'mobile_equipment_interaction_review',
    evidence_supports_review:
      'Supports review of pedestrian-forklift separation controls in the configured area. Field context and existing controls require confirmation.',
    investigation_alignment: {
      occurrence_families: ['Struck-by mobile equipment', 'Crushed-by / caught-between mobile equipment'],
      cause_patterns: [
        'pedestrian route crosses mobile-equipment path',
        'restricted-zone separation breakdown',
        'visibility or traffic-management gap',
      ],
      contributing_factor_themes: [
        'pedestrian separation',
        'route design',
        'barriers or physical separation',
        'traffic management and supervision',
      ],
      ux_summary: 'Aligned to struck-by / caught-between mobile-equipment investigation themes.',
      report_summary:
        'Comparable to struck-by, crushed-by, or caught-between mobile-equipment themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven. It does not prove near-miss severity or mobile-equipment causation.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  forklift_proximity: {
    scan_template: 'forklift_proximity',
    exposure_category: 'forklift_proximity',
    risk_family: 'struck_by_mobile_equipment',
    hazard_exposure_label: 'Pedestrian–lift-truck proximity exposure',
    short_exposure_label: 'Pedestrian–lift-truck proximity',
    control_review_focus: [
      'pedestrian / lift-truck separation',
      'aisle and cross-aisle traffic rules',
      'controlled crossing design',
      'replenishment and staging routing',
      'visibility at turns',
      'barriers or physical separation',
      'supervisor review during peak movement',
    ],
    claim_boundary_profile: 'forklift_proximity_review',
    evidence_supports_review:
      'Supports review of pedestrian–lift-truck separation and traffic controls when both are present in the same camera. Geometric proximity observation only — not causation or severity. Field context and existing controls require confirmation.',
    investigation_alignment: {
      occurrence_families: ['Struck-by mobile equipment', 'Crushed-by / caught-between mobile equipment'],
      cause_patterns: [
        'pedestrian and lift truck share the same travel path',
        'cross-aisle or replenishment routing creates sustained pairing',
        'visibility or traffic-management gap at shared crossings',
      ],
      contributing_factor_themes: [
        'pedestrian / lift-truck separation',
        'aisle and cross-aisle route design',
        'barriers or physical separation',
        'traffic management and supervision',
      ],
      ux_summary:
        'Aligned to struck-by / caught-between mobile-equipment investigation themes for proximity pairing — not zone-intrusion alone.',
      report_summary:
        'Comparable to struck-by or caught-between mobile-equipment themes when reviewing prevention context for same-camera proximity pairing.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven. It does not rate interaction severity or establish collision causation.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  forklift_in_pedestrian_zone: {
    scan_template: 'mobile_equipment_interaction',
    exposure_category: 'forklift_in_pedestrian_zone',
    risk_family: 'struck_by_mobile_equipment',
    hazard_exposure_label: 'Forklift activity in pedestrian-priority zone',
    short_exposure_label: 'Forklift activity in pedestrian zone',
    control_review_focus: [
      'pedestrian-priority area controls',
      'forklift routing',
      'traffic rules',
      'physical separation',
      'signage',
      'supervisor review',
    ],
    claim_boundary_profile: 'mobile_equipment_interaction_review',
    investigation_alignment: {
      occurrence_families: ['Struck-by mobile equipment', 'Crushed-by / caught-between mobile equipment'],
      cause_patterns: [
        'vehicle entered pedestrian-priority space',
        'traffic-rule breakdown',
        'visibility or route-control gap',
      ],
      contributing_factor_themes: [
        'pedestrian-priority area controls',
        'forklift routing',
        'physical separation',
        'traffic management and supervision',
      ],
      ux_summary: 'Aligned to struck-by / caught-between mobile-equipment investigation themes.',
      report_summary:
        'Comparable to struck-by, crushed-by, or caught-between mobile-equipment themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven. It does not prove near-miss severity or mobile-equipment causation.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  forklift_in_restricted_area: {
    scan_template: 'mobile_equipment_interaction',
    exposure_category: 'forklift_in_restricted_area',
    risk_family: 'vehicle_restricted_area_access',
    hazard_exposure_label: 'Forklift activity in configured restricted area',
    short_exposure_label: 'Forklift activity in restricted area',
    control_review_focus: [
      'vehicle access controls',
      'restricted-area boundary',
      'traffic management',
      'signage',
      'barriers',
      'supervisor review',
    ],
    claim_boundary_profile: 'mobile_equipment_interaction_review',
    investigation_alignment: {
      occurrence_families: ['Struck-by mobile equipment', 'Vehicle access to restricted area'],
      cause_patterns: [
        'vehicle entered restricted space',
        'traffic management gap',
        'restricted-area boundary breakdown',
      ],
      contributing_factor_themes: [
        'vehicle access controls',
        'restricted-area boundary',
        'traffic management',
        'barriers and supervision',
      ],
      ux_summary: 'Aligned to mobile-equipment restricted-access investigation themes.',
      report_summary:
        'Comparable to vehicle access-control and struck-by mobile-equipment themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven. It does not prove near-miss severity or mobile-equipment causation.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  ppe_compliance: {
    scan_template: 'ppe_procedural_control',
    exposure_category: 'missing_required_ppe',
    risk_family: 'procedural_control_adherence',
    hazard_exposure_label: 'Missing required PPE signals',
    short_exposure_label: 'Missing required PPE',
    control_review_focus: [
      'PPE requirement communication',
      'signage',
      'supervisor review',
      'area/shift trend review',
      'access or task procedure',
      'training follow-up',
    ],
    claim_boundary_profile: 'ppe_procedural_control_review',
    evidence_supports_review:
      'Supports review of PPE procedural-control adherence, area/shift trends, and supervisor follow-up. This is not a worker-performance finding.',
    investigation_alignment: {
      occurrence_families: ['PPE procedural-control review'],
      cause_patterns: ['requirement communication gap', 'PPE availability or access gap', 'supervisor follow-up gap'],
      contributing_factor_themes: [
        'PPE requirement communication',
        'signage',
        'access or task procedure',
        'supervisor review and training follow-up',
      ],
      ux_summary: 'Aligned to PPE procedural-control investigation themes, not worker fault.',
      report_summary:
        'Comparable to PPE procedural-control and supervisory follow-up themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven. It does not determine regulatory PPE compliance or worker misconduct.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  ergonomics_material_handling: {
    scan_template: 'ergonomics_material_handling',
    exposure_category: 'ergonomic_exposure_signals',
    risk_family: 'msi_material_handling',
    hazard_exposure_label: 'Recurring ergonomic exposure signals during material-handling tasks',
    short_exposure_label: 'Ergonomic exposure during material handling',
    control_review_focus: [
      'workstation layout',
      'lift height',
      'reach distance',
      'material staging',
      'task rotation',
      'manual-handling controls',
    ],
    claim_boundary_profile: 'ergonomics_exposure_review',
    evidence_supports_review:
      'Supports review of ergonomic exposure signals during material-handling tasks. This is not an injury diagnosis or productivity measurement.',
    investigation_alignment: {
      occurrence_families: ['MSI / material-handling exposure review'],
      cause_patterns: [
        'lift height or reach-distance demand',
        'material staging or workstation layout',
        'repetition or manual-handling load pattern',
      ],
      contributing_factor_themes: [
        'workstation layout',
        'lift height and reach distance',
        'material staging',
        'task rotation and manual-handling controls',
      ],
      ux_summary: 'Aligned to MSI / material-handling investigation themes for ergonomic exposure triage.',
      report_summary:
        'Comparable to MSI and material-handling prevention themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven. It is not an injury diagnosis, ergonomic assessment, or productivity judgment.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  work_at_height: {
    scan_template: 'work_at_height',
    exposure_category: 'configured_elevated_work_area_exposure',
    risk_family: 'falls_from_elevation',
    hazard_exposure_label:
      'Recurring work-at-height exposure signals in configured elevated work area',
    short_exposure_label: 'Work-at-height exposure',
    control_review_focus: [
      'fall-prevention controls',
      'edge protection',
      'access method',
      'ladder/scaffold context',
      'barricades',
      'signage',
      'supervision',
      'work planning',
    ],
    claim_boundary_profile: 'work_at_height_exposure_review',
    evidence_supports_review:
      'Supports review of fall-prevention controls, edge protection, and access method. This is not a compliance determination or work-area certification.',
    investigation_alignment: {
      occurrence_families: ['Fall from elevation exposure review'],
      cause_patterns: [
        'edge-protection gap',
        'access-method or ladder/scaffold context',
        'work-planning or barricade gap',
      ],
      contributing_factor_themes: [
        'fall-prevention controls',
        'edge protection',
        'access method',
        'barricades, supervision, and work planning',
      ],
      ux_summary: 'Aligned to fall-from-elevation investigation themes for work-at-height exposure review.',
      report_summary:
        'Comparable to fall-from-elevation investigation themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven. It does not determine fall-protection compliance or certify the work area.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  work_at_height_template: {
    scan_template: 'work_at_height',
    exposure_category: 'configured_elevated_work_area_exposure',
    risk_family: 'falls_from_elevation',
    hazard_exposure_label:
      'Recurring work-at-height exposure signals in configured elevated work area',
    short_exposure_label: 'Work-at-height exposure',
    control_review_focus: [
      'fall-prevention controls',
      'edge protection',
      'access method',
      'ladder/scaffold context',
      'barricades',
      'signage',
      'supervision',
      'work planning',
    ],
    claim_boundary_profile: 'work_at_height_exposure_review',
    evidence_supports_review:
      'Supports review of fall-prevention controls, edge protection, and access method. This is not a compliance determination or work-area certification.',
    investigation_alignment: {
      occurrence_families: ['Fall from elevation exposure review'],
      cause_patterns: [
        'edge-protection gap',
        'access-method or ladder/scaffold context',
        'work-planning or barricade gap',
      ],
      contributing_factor_themes: [
        'fall-prevention controls',
        'edge protection',
        'access method',
        'barricades, supervision, and work planning',
      ],
      ux_summary: 'Aligned to fall-from-elevation investigation themes for work-at-height exposure review.',
      report_summary:
        'Comparable to fall-from-elevation investigation themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven. It does not determine fall-protection compliance or certify the work area.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
  material_load_interaction: {
    scan_template: 'material_load_interaction',
    exposure_category: 'material_load_interaction',
    risk_family: 'load_handling_interaction',
    hazard_exposure_label: 'Material-load interaction exposure in configured work area',
    short_exposure_label: 'Material-load interaction exposure',
    control_review_focus: [
      'load path separation',
      'staging layout',
      'visibility and spotting',
      'travel route design',
      'barriers and exclusion zones',
      'lift-plan supervision',
    ],
    claim_boundary_profile: 'mobile_equipment_interaction_review',
    evidence_supports_review:
      'Supports review of load-path separation, staging layout, and visibility controls. Field context and existing controls require confirmation.',
    investigation_alignment: {
      occurrence_families: ['Struck-by moving load', 'Crushed-by / caught-between load interaction'],
      cause_patterns: [
        'load path crosses worker route',
        'staging or exclusion-zone breakdown',
        'visibility, spotting, or route-control gap',
      ],
      contributing_factor_themes: [
        'load path separation',
        'staging layout',
        'visibility and spotting',
        'barriers, exclusion zones, and lift-plan supervision',
      ],
      ux_summary: 'Registry/export alignment for material-load interaction investigation themes.',
      report_summary:
        'Comparable to struck-by, crushed-by, or caught-between load-handling themes when screening an investigation record.',
      claim_boundary:
        'This alignment supports investigation triage only. It does not determine root cause, fault, compliance, or whether a contributing factor is proven. It does not prove load causation, rated-lift compliance, or impact severity.',
      source_label: 'Curated WorkSafeBC IIR alignment',
    },
  },
};

const CLAIMS_ROI_BASE_BOUNDARY =
  'Claims / ROI alignment is provided for prioritization and prevention-context review only. It does not estimate savings, predict claim reduction, determine claim causation, determine regulatory compliance, or prove that a scan prevented an injury or claim.';

const CLAIMS_ROI_MIRROR: Record<string, ClaimsRoiAlignmentContextBlock> = {
  zone_intrusion: {
    claims_alignment_category: 'Restricted / hazardous area exposure',
    claims_cost_driver_family: 'Risk-control support category',
    commercial_priority: 'medium-high',
    roi_rationale:
      'Restricted-area exposure may indicate gaps in boundary clarity, access procedure, workflow routing, supervision, or physical separation. This scan supports review of recurring access-control exposure patterns before they become higher-severity events.',
    ux_summary: 'Commonly relevant where recurring restricted-area access exposure may signal control-review gaps.',
    report_summary: 'Aligned to restricted / hazardous-area exposure categories for prevention-context prioritization.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No claim that entry itself caused or would cause injury.`,
    target_industry_hints: ['manufacturing', 'warehousing / distribution', 'food processing'],
    industry_sector_hints: ['manufacturing', 'warehousing / distribution', 'food processing'],
    commercial_priority_basis: ['control-review clarity', 'visual detectability', 'deployment ease'],
    claim_characteristic_hints: ['risk-control support', 'medium-high hazard potential'],
    recommended_first_scan: 'Restricted Area Exposure Scan',
    recommended_pilot_templates: ['Restricted Area Exposure Scan', 'Mobile Equipment Interaction Scan'],
    buyer_pain_language: 'Recurring access-control exposure in configured hazardous or restricted areas.',
    sales_discovery_questions: [
      'Which restricted areas drive the most recurring access exposure?',
      'Are boundaries, signage, and workflow routing reviewed after near-misses?',
    ],
    benefit_type_relevance: ['time-loss'],
  },
  person_in_restricted_area: {
    claims_alignment_category: 'Restricted / hazardous area exposure',
    claims_cost_driver_family: 'Risk-control support category',
    commercial_priority: 'medium-high',
    roi_rationale:
      'Restricted-area exposure may indicate gaps in boundary clarity, access procedure, workflow routing, supervision, or physical separation. This scan supports review of recurring access-control exposure patterns before they become higher-severity events.',
    ux_summary: 'Commonly relevant where recurring restricted-area access exposure may signal control-review gaps.',
    report_summary: 'Aligned to restricted / hazardous-area exposure categories for prevention-context prioritization.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No claim that entry itself caused or would cause injury.`,
    target_industry_hints: ['manufacturing', 'warehousing / distribution', 'food processing'],
    industry_sector_hints: ['manufacturing', 'warehousing / distribution', 'food processing'],
    commercial_priority_basis: ['control-review clarity', 'visual detectability', 'deployment ease'],
    claim_characteristic_hints: ['risk-control support', 'medium-high hazard potential'],
    recommended_first_scan: 'Restricted Area Exposure Scan',
    recommended_pilot_templates: ['Restricted Area Exposure Scan', 'Mobile Equipment Interaction Scan'],
    buyer_pain_language: 'Recurring access-control exposure in configured hazardous or restricted areas.',
    sales_discovery_questions: [
      'Which restricted areas drive the most recurring access exposure?',
      'Are boundaries, signage, and workflow routing reviewed after near-misses?',
    ],
    benefit_type_relevance: ['time-loss'],
  },
  pedestrian_in_forklift_zone: {
    claims_alignment_category: 'Mobile-equipment / vehicle / struck-by exposure',
    claims_cost_driver_family: 'High-severity and potentially high-cost category',
    commercial_priority: 'high',
    roi_rationale:
      'Mobile-equipment and vehicle-interaction exposures can carry high direct and indirect cost. This scan supports review of recurring pedestrian/equipment exposure, restricted-zone access, traffic separation, route design, signage, barriers, supervisor follow-up, and matched observation over time.',
    ux_summary: 'Commonly relevant where mobile-equipment interaction is a high-severity commercial pain category.',
    report_summary: 'Aligned to mobile-equipment / struck-by exposure categories for prevention-context review.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No fault assignment, causation determination, or safety-rated control claim.`,
    target_industry_hints: ['warehousing / distribution', 'manufacturing', 'logistics / trucking yards'],
    industry_sector_hints: ['warehousing / distribution', 'manufacturing', 'logistics / trucking yards'],
    commercial_priority_basis: ['claim cost / severity', 'visual detectability', 'control-review clarity', 'buyer urgency'],
    claim_characteristic_hints: ['high severity', 'high cost', 'fatal/catastrophic potential'],
    recommended_first_scan: 'Mobile Equipment Interaction Scan',
    recommended_pilot_templates: ['Mobile Equipment Interaction Scan', 'Walkway / Housekeeping Exposure Scan'],
    buyer_pain_language: 'Recurring pedestrian and mobile-equipment interaction exposure in configured zones.',
    sales_discovery_questions: [
      'Where do pedestrians and mobile equipment most often share space?',
      'What separation controls are documented versus observed in practice?',
    ],
    benefit_type_relevance: ['time-loss', 'fatal/survivor'],
  },
  forklift_in_pedestrian_zone: {
    claims_alignment_category: 'Mobile-equipment / vehicle / struck-by exposure',
    claims_cost_driver_family: 'High-severity and potentially high-cost category',
    commercial_priority: 'high',
    roi_rationale:
      'Mobile-equipment and vehicle-interaction exposures can carry high direct and indirect cost. This scan supports review of recurring pedestrian/equipment exposure, restricted-zone access, traffic separation, route design, signage, barriers, supervisor follow-up, and matched observation over time.',
    ux_summary: 'Commonly relevant where mobile-equipment interaction is a high-severity commercial pain category.',
    report_summary: 'Aligned to mobile-equipment / struck-by exposure categories for prevention-context review.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No fault assignment, causation determination, or safety-rated control claim.`,
    target_industry_hints: ['warehousing / distribution', 'manufacturing', 'logistics / trucking yards'],
    industry_sector_hints: ['warehousing / distribution', 'manufacturing', 'logistics / trucking yards'],
    commercial_priority_basis: ['claim cost / severity', 'visual detectability', 'control-review clarity'],
    claim_characteristic_hints: ['high severity', 'high cost'],
    recommended_first_scan: 'Mobile Equipment Interaction Scan',
    recommended_pilot_templates: ['Mobile Equipment Interaction Scan'],
    buyer_pain_language: 'Recurring pedestrian and mobile-equipment interaction exposure in configured zones.',
    sales_discovery_questions: [
      'Where do pedestrians and mobile equipment most often share space?',
      'What separation controls are documented versus observed in practice?',
    ],
    benefit_type_relevance: ['time-loss', 'fatal/survivor'],
  },
  forklift_in_restricted_area: {
    claims_alignment_category: 'Mobile-equipment / vehicle / struck-by exposure',
    claims_cost_driver_family: 'High-severity and potentially high-cost category',
    commercial_priority: 'high',
    roi_rationale:
      'Mobile-equipment and vehicle-interaction exposures can carry high direct and indirect cost. This scan supports review of recurring pedestrian/equipment exposure, restricted-zone access, traffic separation, route design, signage, barriers, supervisor follow-up, and matched observation over time.',
    ux_summary: 'Commonly relevant where mobile-equipment interaction is a high-severity commercial pain category.',
    report_summary: 'Aligned to mobile-equipment / struck-by exposure categories for prevention-context review.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No fault assignment, causation determination, or safety-rated control claim.`,
    target_industry_hints: ['warehousing / distribution', 'manufacturing', 'municipal yards'],
    industry_sector_hints: ['warehousing / distribution', 'manufacturing', 'logistics / trucking yards'],
    commercial_priority_basis: ['claim cost / severity', 'visual detectability', 'control-review clarity'],
    claim_characteristic_hints: ['high severity', 'high cost'],
    recommended_first_scan: 'Mobile Equipment Interaction Scan',
    recommended_pilot_templates: ['Mobile Equipment Interaction Scan'],
    buyer_pain_language: 'Recurring pedestrian and mobile-equipment interaction exposure in configured zones.',
    sales_discovery_questions: [
      'Where do pedestrians and mobile equipment most often share space?',
      'What separation controls are documented versus observed in practice?',
    ],
    benefit_type_relevance: ['time-loss', 'fatal/survivor'],
  },
  ppe_compliance: {
    claims_alignment_category: 'PPE procedural-control adherence',
    claims_cost_driver_family: 'Procedural-control support category',
    commercial_priority: 'medium',
    roi_rationale:
      'Missing-required-PPE signals can indicate procedural-control gaps in requirement communication, supervisor follow-up, and task-entry controls. This scan supports review without treating PPE as the underlying hazard control.',
    ux_summary: 'Commonly relevant where PPE procedural-control adherence is a recurring review theme.',
    report_summary: 'Aligned to PPE procedural-control categories for prevention-context prioritization, not worker fault.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} PPE compliance alone does not control the underlying hazard.`,
    target_industry_hints: ['manufacturing', 'food processing', 'healthcare / care facilities'],
    industry_sector_hints: ['manufacturing', 'food processing', 'healthcare / care facilities'],
    commercial_priority_basis: ['control-review clarity', 'visual detectability', 'deployment ease'],
    claim_characteristic_hints: ['procedural-control recurrence', 'broad industry recurrence'],
    recommended_first_scan: 'PPE Procedural Control Scan',
    recommended_pilot_templates: ['PPE Procedural Control Scan'],
    buyer_pain_language: 'Recurring missing-required-PPE signals in configured review areas.',
    sales_discovery_questions: [
      'Which tasks require PPE gating at entry?',
      'How are PPE requirements communicated to rotating crews?',
    ],
    benefit_type_relevance: ['time-loss'],
  },
  ergonomics_material_handling: {
    claims_alignment_category: 'MSI / ergonomics / material handling',
    claims_cost_driver_family: 'High-frequency injury / claim-cost category',
    commercial_priority: 'high',
    roi_rationale:
      'MSI and material-handling exposures can create recurring claim burden across many workplaces. This scan supports review of visual exposure patterns such as awkward posture, repeated bending, overreaching, material staging, reach distance, lift height, and task layout.',
    ux_summary: 'Commonly relevant where MSI and material-handling burden is a high-frequency commercial pain category.',
    report_summary: 'Aligned to MSI / ergonomics / material-handling categories for prevention-context prioritization.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No injury diagnosis, ergonomic compliance determination, or claim-cost reduction estimate.`,
    target_industry_hints: ['manufacturing', 'food processing', 'warehousing / distribution'],
    industry_sector_hints: ['manufacturing', 'food processing', 'warehousing / distribution'],
    commercial_priority_basis: ['claim frequency', 'visual detectability', 'control-review clarity', 'deployment ease', 'buyer urgency'],
    claim_characteristic_hints: ['high frequency', 'long-duration / disability burden'],
    recommended_first_scan: 'Ergonomics / Material Handling Scan',
    recommended_pilot_templates: ['Ergonomics / Material Handling Scan', 'Walkway / Housekeeping Exposure Scan'],
    buyer_pain_language: 'Recurring ergonomic exposure during material-handling tasks.',
    sales_discovery_questions: [
      'Which material-handling tasks generate the most MSI reports or discomfort signals?',
      'Are lift heights and reach distances reviewed during workstation changes?',
    ],
    benefit_type_relevance: ['STD', 'LTD', 'time-loss'],
  },
  work_at_height: {
    claims_alignment_category: 'Falls from elevation / work-at-height exposure',
    claims_cost_driver_family: 'High-severity injury category',
    commercial_priority: 'high severity / targeted market',
    roi_rationale:
      'Falls from elevation are high-severity events and can be commercially significant even when less frequent than broad injury categories. This scan supports review of configured elevated-work exposure, access method, edge protection, barricades, supervision, work planning, and follow-up observation.',
    ux_summary: 'Commonly relevant where fall-from-elevation exposure is a high-severity commercial pain category.',
    report_summary: 'Aligned to work-at-height / falls-from-elevation categories for targeted prevention-context review.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No automatic height measurement, fall-protection compliance determination, or work-area certification.`,
    target_industry_hints: ['construction', 'industrial maintenance', 'facilities / municipal operations'],
    industry_sector_hints: ['construction', 'industrial maintenance', 'facilities / municipal operations'],
    commercial_priority_basis: ['claim cost / severity', 'control-review clarity', 'visual detectability', 'buyer urgency'],
    claim_characteristic_hints: ['high severity', 'fatal/catastrophic potential'],
    recommended_first_scan: 'Work At Height Scan',
    recommended_pilot_templates: ['Work At Height Scan', 'Mobile Equipment Interaction Scan'],
    buyer_pain_language: 'Recurring elevated-work exposure signals in configured work areas.',
    sales_discovery_questions: [
      'Which elevated work areas are most frequently accessed without planned controls?',
      'How are edge protection and access methods reviewed after exposure signals?',
    ],
    benefit_type_relevance: ['time-loss', 'fatal/survivor'],
  },
  work_at_height_template: {
    claims_alignment_category: 'Falls from elevation / work-at-height exposure',
    claims_cost_driver_family: 'High-severity injury category',
    commercial_priority: 'high severity / targeted market',
    roi_rationale:
      'Falls from elevation are high-severity events and can be commercially significant even when less frequent than broad injury categories. This scan supports review of configured elevated-work exposure, access method, edge protection, barricades, supervision, work planning, and follow-up observation.',
    ux_summary: 'Commonly relevant where fall-from-elevation exposure is a high-severity commercial pain category.',
    report_summary: 'Aligned to work-at-height / falls-from-elevation categories for targeted prevention-context review.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No automatic height measurement, fall-protection compliance determination, or work-area certification.`,
    target_industry_hints: ['construction', 'industrial maintenance', 'facilities / municipal operations'],
    industry_sector_hints: ['construction', 'industrial maintenance', 'facilities / municipal operations'],
    commercial_priority_basis: ['claim cost / severity', 'control-review clarity', 'visual detectability', 'buyer urgency'],
    claim_characteristic_hints: ['high severity', 'fatal/catastrophic potential'],
    recommended_first_scan: 'Work At Height Scan',
    recommended_pilot_templates: ['Work At Height Scan', 'Mobile Equipment Interaction Scan'],
    buyer_pain_language: 'Recurring elevated-work exposure signals in configured work areas.',
    sales_discovery_questions: [
      'Which elevated work areas are most frequently accessed without planned controls?',
      'How are edge protection and access methods reviewed after exposure signals?',
    ],
    benefit_type_relevance: ['time-loss', 'fatal/survivor'],
  },
  material_load_interaction: {
    claims_alignment_category: 'Load-handling / struck-by load interaction',
    claims_cost_driver_family: 'High-severity interaction category',
    commercial_priority: 'future',
    roi_rationale:
      'Material-load interaction exposures can align to struck-by / crushed-by load-handling themes. This mapping is reserved for future operational templates and gated pilot use.',
    ux_summary: 'Future template mapping for material-load interaction commercial context.',
    report_summary: 'Future alignment for material-load interaction themes; not enabled for active pilot recommendation.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No load causation or rated-lift compliance claim.`,
    claim_characteristic_hints: ['high severity'],
    commercial_priority_basis: ['claim cost / severity', 'control-review clarity'],
    recommended_pilot_templates: [],
    recommended_first_scan: null,
    buyer_pain_language: 'Potential load-path and staging exposure where a dedicated template is enabled.',
  },
  slips_trips_falls_housekeeping: {
    claims_alignment_category: 'Slips / trips / falls',
    claims_cost_driver_family: 'Broad injury-frequency category',
    commercial_priority: 'future',
    roi_rationale:
      'Slips, trips, and falls are broad recurring claim categories. This future scan template would support review of visible walkway, staging, housekeeping, obstruction, congestion, and access-route exposure patterns where cameras can observe the configured area.',
    ux_summary: 'Future template mapping for slips / trips / falls commercial context.',
    report_summary: 'Future alignment for walkway / housekeeping exposure; not enabled for active pilot recommendation.',
    claim_boundary: `${CLAIMS_ROI_BASE_BOUNDARY} No automatic floor-condition or slip-resistance determination.`,
    target_industry_hints: [
      'warehousing / distribution',
      'manufacturing',
      'food processing',
      'retail / facilities',
      'municipal operations',
      'healthcare / care facilities',
    ],
    commercial_priority_basis: ['claim frequency', 'visual detectability', 'deployment ease'],
    claim_characteristic_hints: ['high frequency', 'broad industry recurrence'],
    recommended_pilot_templates: [],
    recommended_first_scan: null,
    buyer_pain_language: 'Recurring walkway, obstruction, or housekeeping exposure patterns.',
    benefit_type_relevance: ['time-loss'],
  },
};

export function buildClaimsRoiContextForAlias(alias: string): ClaimsRoiAlignmentContextBlock | null {
  const key = String(alias || '').trim();
  if (!key) return null;
  return CLAIMS_ROI_MIRROR[key] || null;
}

const SAFETY_CONTROL_REVIEW_BASE_BOUNDARY =
  'This scan does not determine that a control failed, that a person violated a rule, or that the area is non-compliant. It identifies recurring exposure signals that warrant field control review.';
const SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY =
  'Follow-up observations support review, not proof of corrective-action effectiveness. Field confirmation is required before determining whether controls are sufficient.';

const SAFETY_CONTROL_REVIEW_MIRROR: Record<string, SafetyControlReviewContextBlock> = {
  zone_intrusion: {
    control_objective:
      'Prevent routine or unauthorized person entry into the configured restricted or hazardous area during normal operations.',
    existing_controls_to_verify: [
      'floor markings',
      'restricted-zone signage',
      'physical boundary / barrier / gate if present',
      'access-control procedure',
      'supervisor oversight',
      'workflow routing around the zone',
    ],
    potential_control_gaps: [
      'boundary not clear enough',
      'route design pulls workers through the zone',
      'physical separation missing or insufficient',
      'access exceptions not defined',
    ],
    field_verification_questions: [
      'Is entry into the configured area expected, authorized, or routine during normal work?',
      'Are physical boundaries or barriers present and effective?',
      'Are access exceptions defined and supervised?',
    ],
    verification_status: 'pending_field_verification',
    effectiveness_boundary: SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY,
    closure_requirements: [
      'EHS or operations verifies controls are present, adequate, and followed.',
      'Closure is accepted by customer or EHS, not inferred by Edgentik.',
    ],
    ux_summary:
      'Review whether boundary clarity, physical separation, access procedure, and workflow routing are strong enough for the observed restricted-area exposure pattern.',
    report_summary:
      'Observed exposure signals indicate that EHS or operations should review whether restricted-area controls are present, adequate, followed, and strong enough for the configured area.',
    claim_boundary: SAFETY_CONTROL_REVIEW_BASE_BOUNDARY,
    primary_control_lens: 'Engineering/layout review recommended',
    source_label: 'WorkSafeBC-aligned safety control review',
  },
  person_in_restricted_area: {
    control_objective:
      'Prevent routine or unauthorized person entry into the configured restricted or hazardous area during normal operations.',
    existing_controls_to_verify: [
      'floor markings',
      'restricted-zone signage',
      'physical boundary / barrier / gate if present',
      'access-control procedure',
      'supervisor oversight',
    ],
    potential_control_gaps: [
      'boundary not clear enough',
      'route design pulls workers through the zone',
      'physical separation missing or insufficient',
    ],
    field_verification_questions: [
      'Is entry into the configured area expected, authorized, or routine during normal work?',
      'Are physical boundaries or barriers present and effective?',
    ],
    verification_status: 'pending_field_verification',
    effectiveness_boundary: SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY,
    closure_requirements: [
      'EHS or operations verifies controls are present, adequate, and followed.',
      'Closure is accepted by customer or EHS, not inferred by Edgentik.',
    ],
    ux_summary:
      'Review whether boundary clarity, physical separation, access procedure, and workflow routing are strong enough for the observed restricted-area exposure pattern.',
    report_summary:
      'Observed exposure signals indicate that EHS or operations should review whether restricted-area controls are present, adequate, followed, and strong enough for the configured area.',
    claim_boundary: SAFETY_CONTROL_REVIEW_BASE_BOUNDARY,
    primary_control_lens: 'Engineering/layout review recommended',
    source_label: 'WorkSafeBC-aligned safety control review',
  },
  pedestrian_in_forklift_zone: {
    control_objective:
      'Reduce pedestrian and mobile-equipment interaction exposure in configured traffic, forklift, yard, or restricted-equipment areas.',
    existing_controls_to_verify: [
      'pedestrian route separation',
      'forklift or vehicle lanes',
      'traffic rules',
      'barriers, guardrails, or bollards',
      'supervisor oversight',
    ],
    potential_control_gaps: [
      'pedestrian route crosses equipment path',
      'traffic separation is marked but not physically protected',
      'staging or loading workflow pulls pedestrians into equipment zones',
    ],
    field_verification_questions: [
      'Do pedestrians routinely cross or work inside configured equipment traffic paths?',
      'Is separation physical, marked only, or absent in the observed area?',
      'Are supervisors actively managing mixed traffic during peak activity?',
    ],
    verification_status: 'pending_field_verification',
    effectiveness_boundary: SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY,
    closure_requirements: [
      'EHS or operations verifies separation, traffic rules, and supervision are adequate.',
      'Closure is accepted by customer or EHS, not inferred by Edgentik.',
    ],
    ux_summary:
      'Review whether pedestrian/equipment separation, traffic rules, physical barriers, and supervision are strong enough for the observed interaction pattern.',
    report_summary:
      'Observed exposure signals indicate that EHS or operations should review whether mobile-equipment interaction controls are present, adequate, followed, and strong enough for the configured area.',
    claim_boundary: SAFETY_CONTROL_REVIEW_BASE_BOUNDARY,
    primary_control_lens: 'Separation and traffic-control review',
    source_label: 'WorkSafeBC-aligned safety control review',
  },
  ppe_compliance: {
    control_objective:
      'Verify whether required PPE is available, understood, and consistently used for the configured hazard context.',
    existing_controls_to_verify: [
      'posted PPE requirements',
      'PPE availability near the task',
      'supervisor reinforcement',
      'task-specific hazard communication',
    ],
    potential_control_gaps: [
      'PPE requirement not visible or understood',
      'PPE used as substitute for higher-order controls',
      'inconsistent supervisor reinforcement',
    ],
    field_verification_questions: [
      'Is the required PPE clearly posted and understood for this task?',
      'Is PPE available at the point of use when needed?',
      'Is PPE being relied on where engineering or administrative controls should apply?',
    ],
    verification_status: 'pending_field_verification',
    effectiveness_boundary: SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY,
    closure_requirements: [
      'EHS or operations verifies PPE requirements and supporting controls are adequate.',
      'Closure is accepted by customer or EHS, not inferred by Edgentik.',
    ],
    ux_summary:
      'Review whether PPE requirements, availability, and reinforcement match the observed exposure pattern without treating PPE as the only control.',
    report_summary:
      'Observed exposure signals indicate that EHS or operations should review whether PPE and supporting controls are present, adequate, followed, and strong enough for the configured hazard context.',
    claim_boundary: `${SAFETY_CONTROL_REVIEW_BASE_BOUNDARY} PPE compliance alone does not control the underlying hazard.`,
    primary_control_lens: 'Administrative and PPE verification',
    source_label: 'WorkSafeBC-aligned safety control review',
  },
  ergonomics_material_handling: {
    control_objective:
      'Reduce MSI / material-handling exposure by reviewing task design, aids, staging, and work practices in the configured area.',
    existing_controls_to_verify: [
      'lift aids or mechanical assists',
      'staging and reach distances',
      'task rotation or job design',
      'training and safe-work expectations',
      'supervisor oversight',
    ],
    potential_control_gaps: [
      'manual handling where mechanical aids could apply',
      'awkward reach or staging layout',
      'inconsistent task rotation or team lift practice',
    ],
    field_verification_questions: [
      'Are workers performing manual lifts or reaches that could be redesigned or assisted?',
      'Are mechanical aids available and used where expected?',
      'Does staging or layout force awkward postures or carries?',
    ],
    verification_status: 'pending_field_verification',
    effectiveness_boundary: SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY,
    closure_requirements: [
      'EHS or operations verifies task design and handling controls are adequate.',
      'Closure is accepted by customer or EHS, not inferred by Edgentik.',
    ],
    ux_summary:
      'Review whether material-handling task design, aids, staging, and supervision are strong enough for the observed MSI exposure pattern.',
    report_summary:
      'Observed exposure signals indicate that EHS or operations should review whether material-handling controls are present, adequate, followed, and strong enough for the configured area.',
    claim_boundary: `${SAFETY_CONTROL_REVIEW_BASE_BOUNDARY} No injury diagnosis, ergonomic compliance determination, or claim-cost reduction estimate.`,
    primary_control_lens: 'Task design and handling aids',
    source_label: 'WorkSafeBC-aligned safety control review',
  },
  work_at_height: {
    control_objective:
      'Reduce fall-from-elevation exposure by reviewing access method, edge protection, barricades, and work planning in the configured elevated work area.',
    existing_controls_to_verify: [
      'fall-prevention controls',
      'edge protection',
      'access method',
      'barricades and signage',
      'supervision and work planning',
    ],
    potential_control_gaps: [
      'edge-protection gap',
      'access method or ladder/scaffold context unclear',
      'work-planning or barricade gap',
    ],
    field_verification_questions: [
      'Is the access method appropriate and controlled for the observed work?',
      'Is edge protection present and adequate for the configured area?',
      'Are barricades, signage, and supervision consistent with the work plan?',
    ],
    verification_status: 'pending_field_verification',
    effectiveness_boundary: SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY,
    closure_requirements: [
      'EHS or operations verifies fall-prevention controls are adequate.',
      'Closure is accepted by customer or EHS, not inferred by Edgentik.',
    ],
    ux_summary:
      'Review whether fall-prevention controls, edge protection, access method, and work planning are strong enough for the observed work-at-height exposure pattern.',
    report_summary:
      'Observed exposure signals indicate that EHS or operations should review whether work-at-height controls are present, adequate, followed, and strong enough for the configured area.',
    claim_boundary: `${SAFETY_CONTROL_REVIEW_BASE_BOUNDARY} No automatic height measurement, fall-protection compliance determination, or work-area certification.`,
    primary_control_lens: 'Fall-prevention and access-method review',
    source_label: 'WorkSafeBC-aligned safety control review',
  },
  work_at_height_template: {
    control_objective:
      'Reduce fall-from-elevation exposure by reviewing access method, edge protection, barricades, and work planning in the configured elevated work area.',
    existing_controls_to_verify: [
      'fall-prevention controls',
      'edge protection',
      'access method',
      'barricades and signage',
    ],
    potential_control_gaps: [
      'edge-protection gap',
      'access method or ladder/scaffold context unclear',
    ],
    field_verification_questions: [
      'Is edge protection present and adequate for the configured area?',
      'Is the access method appropriate and controlled for the observed work?',
    ],
    verification_status: 'pending_field_verification',
    effectiveness_boundary: SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY,
    closure_requirements: [
      'EHS or operations verifies fall-prevention controls are adequate.',
      'Closure is accepted by customer or EHS, not inferred by Edgentik.',
    ],
    ux_summary:
      'Review whether fall-prevention controls, edge protection, access method, and work planning are strong enough for the observed work-at-height exposure pattern.',
    report_summary:
      'Observed exposure signals indicate that EHS or operations should review whether work-at-height controls are present, adequate, followed, and strong enough for the configured area.',
    claim_boundary: `${SAFETY_CONTROL_REVIEW_BASE_BOUNDARY} No automatic height measurement, fall-protection compliance determination, or work-area certification.`,
    primary_control_lens: 'Fall-prevention and access-method review',
    source_label: 'WorkSafeBC-aligned safety control review',
  },
  material_load_interaction: {
    control_objective: 'Future template mapping for material-load interaction control review.',
    existing_controls_to_verify: [
      'load-handling procedure',
      'exclusion zones',
      'spotter or signal-person practice',
    ],
    potential_control_gaps: [
      'workers inside swing or drop zones',
      'load path crosses pedestrian routes',
    ],
    field_verification_questions: [
      'Is a dedicated material-load interaction template enabled for this area?',
    ],
    verification_status: 'future_template',
    effectiveness_boundary: SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY,
    closure_requirements: ['Future template only; active closure workflow not enabled.'],
    ux_summary: 'Future template mapping for material-load interaction control review.',
    report_summary: 'Future alignment for load-handling exposure themes.',
    claim_boundary: `${SAFETY_CONTROL_REVIEW_BASE_BOUNDARY} No load causation or rated-lift compliance claim.`,
    primary_control_lens: 'Future template',
    source_label: 'WorkSafeBC-aligned safety control review',
  },
  slips_trips_falls_housekeeping: {
    control_objective: 'Future template mapping for slips, trips, and falls housekeeping control review.',
    existing_controls_to_verify: [
      'walkway clarity',
      'housekeeping routine',
      'staging and obstruction control',
    ],
    potential_control_gaps: [
      'walkway obstruction or congestion',
      'housekeeping inconsistency',
    ],
    field_verification_questions: [
      'Is a dedicated walkway or housekeeping template enabled for this area?',
    ],
    verification_status: 'future_template',
    effectiveness_boundary: SAFETY_CONTROL_REVIEW_EFFECTIVENESS_BOUNDARY,
    closure_requirements: ['Future template only; active closure workflow not enabled.'],
    ux_summary: 'Future template mapping for slips, trips, and falls control review.',
    report_summary: 'Future alignment for walkway and housekeeping exposure themes.',
    claim_boundary: `${SAFETY_CONTROL_REVIEW_BASE_BOUNDARY} No automatic floor-condition or slip-resistance determination.`,
    primary_control_lens: 'Future template',
    source_label: 'WorkSafeBC-aligned safety control review',
  },
};

export function buildSafetyControlReviewForAlias(alias: string): SafetyControlReviewContextBlock | null {
  const key = String(alias || '').trim();
  if (!key) return null;
  return SAFETY_CONTROL_REVIEW_MIRROR[key] || null;
}

export function buildReportContextForAlias(alias: string): PolicyReportContextBlock | null {
  const key = String(alias || '').trim();
  if (!key) return null;
  const base = REPORT_CONTEXT_BY_ALIAS[key];
  if (!base) return null;
  const claims = buildClaimsRoiContextForAlias(key);
  const safetyControl = buildSafetyControlReviewForAlias(key);
  if (!claims && !safetyControl) return base;
  return {
    ...base,
    ...(claims ? { claims_roi_alignment: claims } : {}),
    ...(safetyControl ? { safety_control_review: safetyControl } : {}),
  };
}

export function withPolicyReportContext(
  config: Record<string, any> | undefined,
  uiContext: Required<Pick<PolicyUiContext, 'pack_key' | 'policy_alias' | 'version'>>,
): Record<string, any> {
  const next = withPolicyUiContext(config, uiContext);
  const reportContext = buildReportContextForAlias(uiContext.policy_alias);
  if (!reportContext) return next;
  const existing =
    next.report_context && typeof next.report_context === 'object'
      ? { ...(next.report_context as Record<string, unknown>) }
      : {};
  return {
    ...next,
    report_context: {
      ...existing,
      ...reportContext,
    },
  };
}

export function isRestrictedZone(zone: Zone): boolean {
  const zoneType = String(zone.zone_type || zone.type || '').trim().toLowerCase();
  const zoneClass = String(zone.zone_class || '').trim().toLowerCase();
  return zone.restricted === true || zoneType === 'restricted' || zoneClass === 'restricted';
}

export function isForkliftZone(zone: Zone): boolean {
  const zoneType = String(zone.zone_type || zone.type || '').trim().toLowerCase();
  const allowed = new Set(
    (Array.isArray(zone.allowed_entity_types) ? zone.allowed_entity_types : [])
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean),
  );
  return zoneType.includes('vehicle') || zoneType.includes('forklift') || (allowed.has('forklift') && !allowed.has('person'));
}

export function isPedestrianZone(zone: Zone): boolean {
  const allowed = new Set(
    (Array.isArray(zone.allowed_entity_types) ? zone.allowed_entity_types : [])
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean),
  );
  return allowed.has('person') && !allowed.has('forklift');
}

export function getScopedZones(zoneIds: unknown, zonesById?: ReadonlyMap<string, Zone>): Zone[] {
  return (Array.isArray(zoneIds) ? zoneIds : [])
    .map((zoneId) => zonesById?.get(String(zoneId)))
    .filter((zone): zone is Zone => Boolean(zone));
}

export function inferZoneIntrusionAlias(subject: PolicySubject, zones: Zone[]): string | null {
  if (zones.length === 0) return null;
  if (subject === 'person' && zones.every((zone) => isForkliftZone(zone))) {
    return 'pedestrian_in_forklift_zone';
  }
  if (subject === 'forklift' && zones.every((zone) => isPedestrianZone(zone))) {
    return 'forklift_in_pedestrian_zone';
  }
  if (subject === 'forklift' && zones.every((zone) => isRestrictedZone(zone))) {
    return 'forklift_in_restricted_area';
  }
  return null;
}

export function inferPolicyAlias(args: {
  policyType: string;
  subject: PolicySubject;
  zoneIds?: unknown;
  zonesById?: ReadonlyMap<string, Zone>;
}): string {
  const { policyType, subject, zoneIds, zonesById } = args;
  const scopedZones = getScopedZones(zoneIds, zonesById);

  if (policyType === 'zone_intrusion') {
    return inferZoneIntrusionAlias(subject, scopedZones) || 'zone_intrusion';
  }
  if (policyType === 'line_crossing' && subject === 'forklift') {
    return 'forklift_line_crossing';
  }
  if (policyType === 'dwell_violation' && subject === 'forklift') {
    return 'forklift_dwell';
  }
  return policyType;
}

export function resolvePackAndAlias(args: {
  policyType: PolicyCreateData['policy_type'];
  subject: PolicySubject;
  zoneIds?: unknown;
  zonesById?: ReadonlyMap<string, Zone>;
  uiContext?: PolicyUiContext | null;
}): { packKey: PackKey; alias: string; inferred: boolean } {
  const { uiContext, policyType, subject, zoneIds, zonesById } = args;
  const persistedAlias = String(uiContext?.policy_alias || '').trim();
  if (persistedAlias && getPolicyAliasDefinition(persistedAlias)) {
    return {
      packKey: getPackKeyForAlias(persistedAlias),
      alias: persistedAlias,
      inferred: false,
    };
  }

  const inferredAlias = inferPolicyAlias({ policyType, subject, zoneIds, zonesById });
  return {
    packKey: getPackKeyForAlias(inferredAlias),
    alias: inferredAlias,
    inferred: true,
  };
}

export function getPolicyLabelFromAliasOrType(args: {
  alias?: string | null;
  policyType: PolicyCreateData['policy_type'];
}): string {
  const aliasDefinition = args.alias ? getPolicyAliasDefinition(args.alias) : undefined;
  if (aliasDefinition) return aliasDefinition.label;
  return GENERIC_POLICY_TYPE_LABELS[args.policyType] || args.policyType;
}
